import { Validators, ValidatorFn } from '@angular/forms';
import { FieldOption, RecordSection } from '@k12/app-components';
import { nameValidator, ssnValidator } from '@k12/validators';

const guardianPidValidator: ValidatorFn = (control) => {
  const value = control.value;
  if (value == null || value === '' || typeof value === 'object') return null;
  return { message: 'Guardian PID not found.' };
};

const validateSsn = ssnValidator();

/** Rejects negative currency entries. Blank is left alone — these amounts are optional. */
const nonNegativeAmount: ValidatorFn = Validators.min(0);

/**
 * Requires a full, well-formed SSN. Blank is left to the edit component, which makes the field
 * required for the admins who can actually see it.
 * @see StudentInformationEditComponent.registerSsnHandler
 */
const studentSsnValidator: ValidatorFn = (control) => {
  const value = control.value;
  if (value == null || value === '') return null;
  return validateSsn(control) ? { message: 'Enter a valid Social Security Number (XXX-XX-XXXX).' } : null;
};

export const STUDENT_INFORMATION_SECTIONS: RecordSection[] = [
  {
    title: 'Student Information',
    fields: [
      {
        key: 'firstName',
        label: 'First Name',
        type: 'text',
        validators: [Validators.required, Validators.maxLength(250), nameValidator()],
        editOnly: true,
        editableBy: ['household']
      },
      { key: 'middleName', label: 'Middle Name', type: 'text', editOnly: true, editableBy: ['household'] },
      {
        key: 'lastName',
        label: 'Last Name',
        type: 'text',
        validators: [Validators.required, Validators.maxLength(250), nameValidator()],
        editOnly: true,
        editableBy: ['household']
      },
      { key: 'dob', label: 'Date of Birth', type: 'date', validators: [Validators.required], editableBy: ['household'] },
      { key: 'ssn', label: 'Social Security', type: 'text', format: 'ssn', placeholder: 'Enter all 9 digits', validators: [studentSsnValidator] },
      { key: 'guardianPid', label: 'Guardian PID', type: 'autocomplete', optionsKey: 'households', asyncSearch: true, editableBy: ['admin'], validators: [guardianPidValidator] },
      { key: 'ethnicity', label: 'Ethnicity', type: 'select', optionsKey: 'ethnicity', editableBy: ['household'] },
      { key: 'race', label: 'Race', type: 'select', optionsKey: 'race', editableBy: ['household'] },
      { key: 'sex', label: 'Sex', type: 'select', optionsKey: 'sex', editableBy: ['household'] },
      { key: 'ncResidency', label: 'NC Resident', type: 'boolean'},
      { key: 'ncResidencyVerified', label: 'Residency Verified', type: 'boolean'} 
    ]
  },
  {
    title: 'Application Details',
    fields: [
      { key: 'osAwardStatus', label: 'Opportunity Scholarship Status', type: 'text', readonly: true },
      {
        key: 'tier',
        label: 'Opportunity Scholarship Tier',
        type: 'text',
        showWhen: { field: 'osAwardStatus', is: ['Eligible', 'Awarded'] },
        readonly: true
      },
      { key: 'esaAwardStatus', label: 'ESA+ Status', type: 'text', readonly: true },
      {
        key: 'esaFundingLevel',
        label: 'Funding Level',
        type: 'text',
        showWhen: { field: 'esaAwardStatus', is: ['Eligible', 'Awarded'] },
        readonly: true
      },
      { key: 'hasHighSchoolDiploma', label: 'Has High School Diploma', type: 'boolean', editableBy: ['admin'] },
      { key: 'enrolledInCollege', label: 'Enrolled in College', type: 'boolean', editableBy: ['admin'] }
    ]
  },
  {
    title: 'School Details',
    fields: [
      { key: 'schoolId',
        label: 'School Name',
        type: 'search-select', optionsKey: 'schools',
        editableBy: ['admin', 'household'],
      },
      { key: 'attendanceTypeId', label: 'Enrollment Status', type: 'select', optionsKey: 'enrollmentTypes', readonly: true },
      { key: 'gradeId',
        label: 'Grade Level',
        type: 'select',
        optionsKey: 'gradeLevels',
        editableBy: ['admin', 'household'],
      },
      { key: 'certificationDate', label: 'Certification Date', type: 'date', readonly: true },
      {
        // Derived from the school's Certification & Endorsement step, so it is never editable here.
        key: 'endorsedBySchoolFall',
        label: 'Fall Term Endorsed by School',
        type: 'boolean',
        readonly: true
      },
      {
        key: 'endorsedBySchoolSpring',
        label: 'Spring Term Endorsed by School',
        type: 'boolean',
        readonly: true
      },
      {
        key: 'homeschoolIdNumber',
        label: 'Homeschool ID Number',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
      {
        key: 'homeschoolName',
        label: 'Homeschool Name',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
      {
        key: 'homeschoolAdministratorName',
        label: 'Homeschool Administrator Name',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
      {
        key: 'homeschoolCounty',
        label: 'Homeschool County',
        type: 'text',
        editableBy: ['household'],
        showWhen: { field: 'schoolTypeId', is: [3, '3'] }
      },
    ]
  },
  {
    title: 'School Cost',
    // Costs and funds are amounts owed or awarded, never credits, so none of them may go below zero.
    fields: [
      { key: 'academicYearStandardCostTotal', label: 'Standard Cost', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'academicYearIndividualCostTotal', label: 'Individual Cost', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'fallCost', label: 'Fall Cost', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'springCost', label: 'Spring Cost', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'cwFallFunds', label: 'Class Wallet Fall Fund', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'cwSpringFunds', label: 'Class Wallet Spring Fund', type: 'currency', validators: [nonNegativeAmount], readonly: true },
      { key: 'cwFundsPaid', label: 'Class Wallet Fund Paid', type: 'currency', validators: [nonNegativeAmount], readonly: true }
    ]
  }
];

export const STUDENT_INFORMATION_OPTIONS: Record<string, FieldOption[]> = {
  yesNo: [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ],
  ethnicity: [
    { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
    { label: 'Not Hispanic or Latino', value: 'Not Hispanic or Latino' }
  ],
  race: [
    { label: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
    { label: 'Asian', value: 'Asian' },
    { label: 'Black or African American', value: 'Black or African American' },
    { label: 'Native Hawaiian or Other Pacific Islander', value: 'Native Hawaiian or Other Pacific Islander' },
    { label: 'White', value: 'White' },
    { label: 'Prefer not to answer', value: 'Prefer not to answer' }
  ],
  sex: [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' }
  ],
  enrollmentTypes: [
    { label: 'Not Yet Certified', value: 0 },
    { label: 'Full Time', value: 1 },
    { label: 'Part Time', value: 2 },
    { label: 'Not Attending', value: 3 }
  ],
  schools: [],
  schoolTypes: [],
  gradeLevels: [],
  households: []
};
